import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import LoadingSpinner from '../UI/LoadingSpinner';
import { 
  TrendingUp, 
  TrendingDown, 
  Minus, 
  Brain, 
  Moon, 
  Target, 
  AlertTriangle,
  CheckCircle,
  Activity,
  Heart
} from 'lucide-react';

const AnalyticsInsights = ({ days = 30 }) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchInsights = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/analytics/insights?days=${days}`);
      setInsights(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching analytics insights:', err);
      setError('Failed to load analytics insights');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchInsights();
  }, [days, fetchInsights]);

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving':
        return <TrendingUp className="w-5 h-5 text-green-500" />;
      case 'declining':
        return <TrendingDown className="w-5 h-5 text-red-500" />;
      default:
        return <Minus className="w-5 h-5 text-gray-500" />;
    }
  };

  const getRiskColor = (riskLevel) => {
    switch (riskLevel) {
      case 'high':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'moderate':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default:
        return 'text-green-600 bg-green-50 border-green-200';
    }
  };

  const getRiskIcon = (riskLevel) => {
    switch (riskLevel) {
      case 'high':
        return <AlertTriangle className="w-5 h-5" />;
      case 'moderate':
        return <AlertTriangle className="w-5 h-5" />;
      default:
        return <CheckCircle className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-center h-32">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="text-center text-red-600">
          <p>{error}</p>
          <button 
            onClick={fetchInsights}
            className="mt-2 text-sm text-blue-600 hover:text-blue-800"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!insights) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="text-center text-gray-500">
          <p>No insights available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Health Analytics Insights</h2>
        <p className="text-gray-600">
          Personalized insights based on your last {days} days of data
        </p>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Mood Trend */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Mood Trend</h3>
            {getTrendIcon(insights.mood_trend.trend)}
          </div>
          <p className="text-2xl font-bold text-gray-800 capitalize">
            {insights.mood_trend.trend}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Based on {insights.mood_trend.values.length} check-ins
          </p>
        </div>

        {/* Risk Level */}
        <div className={`bg-white rounded-lg shadow-sm p-6 border ${getRiskColor(insights.risk_level)}`}>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium">Risk Level</h3>
            {getRiskIcon(insights.risk_level)}
          </div>
          <p className="text-2xl font-bold capitalize">
            {insights.risk_level}
          </p>
          <p className="text-xs mt-1">Overall health assessment</p>
        </div>

        {/* Consistency */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Consistency</h3>
            <Target className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-gray-800">
            {Math.min(insights.consistency.percentage, 100)}%
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {insights.consistency.message}
          </p>
        </div>

        {/* Stress-Mood Correlation */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Stress Impact</h3>
            <Brain className="w-5 h-5 text-purple-500" />
          </div>
          <p className="text-2xl font-bold text-gray-800 capitalize">
            {insights.stress_vs_mood.correlation}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Stress-mood relationship
          </p>
        </div>
      </div>

      {/* Detailed Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sleep Impact */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center mb-4">
            <Moon className="w-6 h-6 text-indigo-500 mr-2" />
            <h3 className="text-lg font-semibold text-gray-800">Sleep Impact</h3>
          </div>
          <p className="text-gray-700 mb-4">{insights.sleep_impact.insight}</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-green-50 rounded">
              <p className="text-sm text-gray-600">Good Sleep Mood</p>
              <p className="text-xl font-bold text-green-600">
                {insights.sleep_impact.good_sleep_avg_mood}
              </p>
            </div>
            <div className="text-center p-3 bg-orange-50 rounded">
              <p className="text-sm text-gray-600">Poor Sleep Mood</p>
              <p className="text-xl font-bold text-orange-600">
                {insights.sleep_impact.poor_sleep_avg_mood}
              </p>
            </div>
          </div>
        </div>

        {/* Stress vs Mood Analysis */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center mb-4">
            <Activity className="w-6 h-6 text-red-500 mr-2" />
            <h3 className="text-lg font-semibold text-gray-800">Stress Analysis</h3>
          </div>
          <p className="text-gray-700 mb-4">{insights.stress_vs_mood.insight}</p>
          <div className="flex items-center justify-center p-4 bg-gray-50 rounded">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-2">Correlation Type</p>
              <p className="text-2xl font-bold capitalize text-gray-800">
                {insights.stress_vs_mood.correlation}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Behavior Insights */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center mb-4">
          <Heart className="w-6 h-6 text-pink-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-800">Behavior Insights</h3>
        </div>
        <div className="space-y-3">
          {insights.behavior_insights.map((insight, index) => (
            <div key={index} className="flex items-start p-3 bg-blue-50 rounded-lg">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
              <p className="text-gray-700">{insight}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsInsights;
